import { useCallback, useEffect, useState } from 'react';

import type { UUID } from '@cobliteam/common-types/src/identifier';
import type { User } from '@cobliteam/common-types/src/user';

import { useFleet } from '@cobliteam/hooks';

import Icon from '@cobliteam/ui/icons';

import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import UserItem from './components/UserItem';
import UserSearch from './components/UserSearch';

interface MultiSelectUsersProps {
  size?: 'sm' | 'md';
  selectedUsersIds: UUID[];
  unselectableOptions?: { option: User; message: string }[];
  title?: string;
  icon?: React.ReactNode;
  onChange: (users: User[]) => void;
  listByEmail?: boolean;
}

const MultiSelectUsers = ({ size = 'md', onChange, title, unselectableOptions, selectedUsersIds: values, icon, listByEmail }: MultiSelectUsersProps) => {
  const [forceClosed, setForceClosed] = useState(false);
  const [isFetchEnabled, setIsFetchEnabled] = useState(false);
  const [selectedUsersIdsDraft, setSelectedUsersIdsDraft] = useState(values || []);
  const [textFilter, setTextFilter] = useState<string>('');

  const { getFleetUsers } = useFleet();
  const { data: users = [], isLoading, isFetching } = getFleetUsers({ refetchOnMount: false, enabled: isFetchEnabled });

  const sortByName = (a: User, b: User) => {
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  };

  const sortBySelected = useCallback(
    (a: User, b: User) => {
      if (values.find((g) => g === a.id) && !values.find((g) => g === b.id)) return -1;
      if (!values.find((g) => g === a.id) && values.find((g) => g === b.id)) return 1;
      return 0;
    },
    [values]
  );

  const openDropdown = useCallback(() => {
    setIsFetchEnabled(true);
    setSelectedUsersIdsDraft(values);
  }, [values]);

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setSelectedUsersIdsDraft(values);
    setTextFilter('');
  }, [values]);

  const handleApply = useCallback(() => {
    const selectedUsers = users.filter((user) => selectedUsersIdsDraft.includes(user.id));
    onChange(selectedUsers);
    handleClose();
  }, [users, onChange, handleClose, selectedUsersIdsDraft]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const toggleItem = useCallback(
    (dataItem: UUID) => {
      const newItemDraft = Array.from(selectedUsersIdsDraft);
      const existItem = newItemDraft.includes(dataItem);
      if (existItem) newItemDraft.splice(newItemDraft.indexOf(dataItem), 1);
      if (!existItem) newItemDraft.push(dataItem);
      setSelectedUsersIdsDraft(newItemDraft);
    },
    [selectedUsersIdsDraft, setSelectedUsersIdsDraft]
  );

  const filteredUsers = users
    .filter((user) => user.name.toLowerCase().includes(textFilter.toLowerCase()))
    .sort(sortByName)
    .sort(sortBySelected);

  const hasSameElements = values?.length === selectedUsersIdsDraft?.length && values.every((value) => selectedUsersIdsDraft?.includes(value));

  return (
    <Dropdown
      onOpenChange={openDropdown}
      forceClose={forceClosed}
      portal={false}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='text' size='sm' disabled={selectedUsersIdsDraft?.length === 0} onClick={() => setSelectedUsersIdsDraft([])}>
              Limpar seleção
            </Button>
          </Dropdown.Header>
          <Dropdown.Search className='px-5'>
            <UserSearch textFilter={textFilter} setTextFilter={setTextFilter} />
          </Dropdown.Search>
          <Dropdown.Body className='pl-5'>
            <Dropdown.List
              loadingComponent={<Dropdown.Loading />}
              data={filteredUsers}
              isLoading={isLoading || isFetching}
              heightList={300}
              heightItem={32}
              itemComponent={(item) => {
                const uselectableMessage = unselectableOptions?.find((option) => option.option.id === item.id)?.message;
                return (
                  <UserItem
                    listByEmail
                    user={item}
                    onChange={() => toggleItem(item.id)}
                    checked={Boolean(selectedUsersIdsDraft.includes(item.id))}
                    unselectableMessage={uselectableMessage}
                  />
                );
              }}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-5'>
              <Button variant='text' full size='sm' onClick={handleClose}>
                Cancelar
              </Button>
              <Button full size='sm' variant='contained' onClick={handleApply} disabled={hasSameElements}>
                Aplicar
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='text' type='button' startIcon={icon || <Icon name='user-round-check' />}>
        {title ? `${title} ${values?.length > 0 ? `(${values?.length})` : ''}` : `Usuários ${values?.length > 0 ? `(${values?.length})` : ''}`}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectUsers;
